<script lang="ts">
  import { onMount } from "svelte"
  import { formatDate, xhr, toast } from "$app/utils"
  import InlineInput from "$app/components/InlineInput.svelte"
  import LoadingSpinner from "$app/components/LoadingSpinner.svelte"
  import Loading from "$app/components/Loading.svelte"
  import EditGoalStatus from "./EditGoalStatus.svelte"
  import DatePicker from "$app/components/DatePicker.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import _ from "lodash"
  import { ChevronRight, ListTodo } from "lucide-svelte"
  import GoalNotes from "./GoalDetails/GoalNotes.svelte"
  import GoalMembers from "./GoalDetails/GoalMembers.svelte"
  import TaskInline from "./GoalDetails/TaskInline.svelte"
  import History from "./GoalDetails/History.svelte"
  import Attachments from "./Attachments/Index.svelte"
  import Progresses from "./Progress/Index.svelte"
  import { post } from "./Task/api"
  import { appStore } from "$app/store.svelte"

  const plan = appStore.plan
  const current_user = appStore.current_user
  const editable: boolean = appStore.editable || appStore.update_goals
  interface Props {
    goal: GoalType
    onupdate?: (goal: GoalType) => void
  }

  let { goal = $bindable(), onupdate }: Props = $props()

  let errors = {}

  let isLoading = $state(true)
  let departments = $state([])
  let tasks = $state([])
  let annualGoals: GoalType[] = []
  let longTermGoals: GoalType[] = []

  let parentGoals: GoalType[] = $state([])

  let name = $state("")
  let description = ""
  let action_plan = ""
  let leadership_notes = ""
  let obstacles_text = ""

  let show_options = $state(false)
  let expected_at = null
  let completed_at = null
  let goal_year = null

  async function initializeForm(): Promise<void> {
    isLoading = true
    try {
      const resp = await xhr.get(`/plans/${plan.id}/goals/${goal.id}/edit.json`)

      goal = resp.data.goal
      name = goal.name
      description = goal.description
      action_plan = goal.action_plan
      leadership_notes = goal.leadership_notes
      obstacles_text = goal.obstacles_text
      departments = resp.data.departments
      if (goal.expected_at) {
        try {
          expected_at = new Date(`${goal.expected_at}T00:00:00`)
          goal_year = expected_at.getFullYear()
        } catch (e) {
          expected_at = null
        }
      }

      if (goal.completed_at) {
        try {
          completed_at = new Date(`${goal.completed_at}T00:00:00`)
        } catch (e) {
          completed_at = null
        }
      }

      annualGoals = _.sortBy(
        resp.data.annual_goals as GoalType[],
        "assignee_position"
      )
      longTermGoals = _.sortBy(
        resp.data.long_term_goals as GoalType[],
        "assignee_position"
      )

      if (goal.type === "AnnualGoal") {
        parentGoals = longTermGoals
      } else if (goal.type === "QuarterlyGoal") {
        parentGoals = annualGoals
      }

      show_options = goal.type === "AnnualGoal" || goal.type === "QuarterlyGoal"

      tasks = resp.data.tasks
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading the goal", "error")
    } finally {
      isLoading = false
    }
  }

  function updateStore(data: GoalType): void {
    const idx = appStore.goals.findIndex((g) => g.id === goal.id)
    try {
      data.expected_at = new Date(`${data.expected_at}T00:00:00`)
    } catch (e) {}
    appStore.goals[idx] = { ...goal, ...data }
    goal = { ...appStore.goals[idx] }
  }

  let saving = $state(false)

  async function handleSave(
    payload: Record<string, any>,
    field?: string
  ): Promise<void> {
    const prevGoal = { ...goal }
    saving = true
    try {
      goal = { ...goal, ...payload }
      const resp = await xhr.patch(`/plans/${plan.id}/goals/${goal.id}.json`, {
        goal: payload
      })

      updateStore(resp.data as GoalType)
      onupdate(resp.data)
    } catch (e) {
      goal = { ...prevGoal }
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast(`There was an error updating ${field ? field : ""}`, "error")
      if (e.response) {
        errors = {
          ...errors,
          ...(e.response.data.errors as Record<string, string>)
        }
      }
    } finally {
      saving = false
    }
  }

  // If the expected_at year changes, we need to reload the parent annual goals
  async function updateExpectedAt(): Promise<void> {
    expected_at = new Date(`${goal.expected_at}T00:00:00`)
    if (expected_at.getFullYear() != goal_year) {
      let new_year = expected_at.getFullYear()
      goal_year = new_year
      try {
        const resp = await xhr.get(
          `/plans/${plan.id}/goals/new.json?year=${new_year}&parent_goal_id=${goal.parent_goal_id}&type=${goal.type}`
        )
        parentGoals = _.sortBy(
          resp.data.goals as GoalType[],
          "assignee_position"
        )
      } catch (e) {
        Honeybadger.notify(e)
        toast("There was an error loading updated annual goals", "error")
      }
    }
  }

  function updateGoal(g: GoalType) {
    const idx = appStore.goals.findIndex((g) => g.id === goal.id)
    appStore.goals[idx] = { ...goal, ...g }
    onupdate(g)
  }

  function createTask(t: TaskType) {
    tasks = [...tasks, t]
  }

  function deleteTask(id: number) {
    tasks = tasks.filter((t) => t.id !== id)
    tasks = tasks
  }

  let creatingTask = $state(false)
  async function addNewTask(): Promise<void> {
    const date = new Date().toJSON().split("T")[0]
    const t = tasks[tasks.length - 1]

    const data = {
      name: "New Task",
      plan_id: plan.id,
      goal_id: goal.id,
      parent_position: t ? t.parent_position : 1,
      status: "on_track",
      assignee_id: goal ? goal.assignee_id : 0,
      assignee_type: goal ? goal.assignee_type : "",
      expected_at: goal.expected_at,
      assignee_position: t ? t.assignee_position : 1
    }

    try {
      creatingTask = true
      const resp = await post(plan.id, data)

      tasks = [{ ...resp.data }, ...tasks]
      //dispatch("createTask", resp.data)
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error creating the new task", "error")
    } finally {
      creatingTask = false
    }
  }

  function updateGoalVisibility() {
    if (
      confirm(
        "Are you sure you want to update visibility? This will also update visibility for any child goals or tasks."
      )
    ) {
      handleSave({ visibility: goal.visibility }, "Goal Visibility")
    }
  }

  let tabs = [
    "Goal Details",
    "Goal Progress",
    "Tasks",
    "Members",
    "History",
    "Attachments"
  ]
  let currentTab = $state(tabs[0])
  let invalidVisibility = $state(false)
  const invalidUsersMsg =
    "There are members that do not belong to Leadership Team, please remove them in the Members tab."

  type eventGoalType = {
    detail: GoalType
  }

  $effect(() => {
    //console.log("goal", goal.user_ids)
    // if (goal?.visibility === "leadership") {
    //   invalidUsers =
    //     goal?.user_ids
    //       ?.map((u_id) => users.find((us) => us.id === u_id))
    //       ?.filter((u) => !isLeadership(u.role)) || []
    //   if (invalidUsers.length > 0) {
    //     invalidVisibility = true
    //     toast(invalidUsersMsg, "error", { duration: 10000 })
    //   }
    // } else {
    //   invalidVisibility = false
    // }
  })

  onMount(async () => {
    initializeForm()
  })
</script>

<div class="mt-2 overflow-visible">
  <div>
    {#if isLoading}
      <LoadingSpinner />
    {:else}
      {#if goal.parent_goal_type === "LongTermGoal" || goal.parent_goal_type === "PlanGoal"}
        <div class="mb-3">
          <span
            class="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-long-term-goal"
          >
            Parent Long-Term Goal
            <ChevronRight class="size-4 text-gray-400 mx-2" />
            <span class="font-bold">{goal.parent_goal_name}</span>
          </span>
        </div>
      {:else if goal.parent_goal_type === "AnnualGoal"}
        <div class="mb-3">
          <span
            class="inline-flex items-center rounded bg-gray-50 px-2 py-0.5 text-xs font-medium text-annual-goal"
          >
            Parent Annual Goal
            <ChevronRight class="size-4 text-gray-400 mx-2" />
            <span class="font-bold">{goal.parent_goal_name}</span>
          </span>
        </div>
      {/if}
      <div class="grid grid-cols-12 gap-4">
        <div
          class="col-span-12 {editable && show_options ? 'lg:col-span-7' : ''}"
        >
          <div class="text-base leading-6 text-gray-900 w-full pr-2">
            <label for="name" class="label text-sm text-gray-600">
              Goal Name
            </label>

            {#if editable}
              <div class="text-lg">
                <InlineInput
                  bind:value={goal.name}
                  onsuccess={(data: GoalType) => onupdate(data)}
                  postURL={`/plans/${plan.id}/goals/${goal.id}.json`}
                  name="name"
                  model="goal"
                />
              </div>
            {:else}
              <div
                class="mt-1 text-sm border rounded border-gray-200 px-3 py-2"
              >
                {name}
              </div>
            {/if}
          </div>
        </div>

        {#if editable && show_options}
          <div class="grid grid-cols-2 gap-3 lg:col-span-5">
            <div>
              <label for="parent_goal_id" class="label text-sm text-gray-600"
                >Parent Goal
              </label>
              <div class="input-cont">
                <select
                  id="parent_goal_id"
                  value={goal.parent_goal_id}
                  onchange={(e: Event) => {
                    handleSave(
                      { parent_goal_id: (e.target as HTMLSelectElement).value },
                      "Parent Goal"
                    )
                  }}
                >
                  {#if parentGoals.length}
                    {#each parentGoals as pg (pg.id)}
                      <option value={pg.id}>{pg.name}</option>
                    {:else}
                      <option value="" disabled>No Parent Goals</option>
                    {/each}
                  {:else}
                    <option value="" disabled>No Parent Goals</option>
                  {/if}
                </select>
              </div>
            </div>
            {#if current_user.role === "leader" || current_user.role === "admin" || current_user.role === "coach"}
              <div>
                <label for="visibility" class="label text-sm text-gray-600">
                  Goal Visibility
                </label>
                {#if goal.parent_visibility == "plan"}
                  <div class="input-cont">
                    <select
                      id="visibility"
                      style={invalidVisibility ? "border: 1px solid red" : ""}
                      bind:value={goal.visibility}
                      onchange={updateGoalVisibility}
                    >
                      <option value="plan">All Members</option>
                      <option value="leadership">Only Leadership Team</option>
                    </select>
                  </div>
                {:else}
                  <div
                    class="mt-1 text-sm border rounded border-gray-200 px-3 py-2"
                  >
                    Parent is Only Leadership Team
                  </div>
                {/if}
              </div>
            {/if}
          </div>
        {/if}
      </div>

      <div class="grid grid-cols-12 gap-5 mt-4">
        {#if show_options}
          <div class="col-span-6 lg:col-span-3">
            <label for="status" class="label text-sm text-gray-600">
              Status
            </label>
            <div class="w-full">
              <EditGoalStatus
                goalOrTask={goal}
                type="Goal"
                {editable}
                onupdate={(d) => updateGoal(d as GoalType)}
              />
            </div>
          </div>

          <div class="col-span-6 lg:col-span-4 lg:pl-4">
            <label for="assignee_id" class="label text-sm text-gray-600">
              Assignee
            </label>
            {#if editable}
              <div class="mt-1 text-sm">
                <select
                  id="assignee_id"
                  value={goal.assignee_id}
                  onchange={(e: Event) => {
                    handleSave(
                      { assignee_id: (e.target as HTMLSelectElement).value },
                      "Assignee"
                    )
                  }}
                >
                  {#if departments.length}
                    {#each departments as dept (dept.id)}
                      <option value={dept.id}>{dept.name}</option>
                    {/each}
                  {:else}
                    <option value="">No Departments</option>
                  {/if}
                </select>
              </div>
            {:else}
              <div
                class="mt-1 text-sm border rounded border-gray-200 px-3 py-2"
              >
                {goal.assignee_name}
              </div>
            {/if}
          </div>

          <div class="grid grid-cols-2 gap-3 lg:col-start-8 lg:col-span-5">
            <div>
              <label for="expected_at" class="label text-sm text-gray-600">
                Expected By
              </label>
              {#if editable}
                <DatePicker
                  date={goal.expected_at}
                  onchange={(d) => handleSave({ expected_at: d })}
                />
              {:else}
                <div
                  class="mt-1 text-sm border rounded border-gray-200 px-3 py-2"
                >
                  {formatDate(goal.expected_at)}
                </div>
              {/if}
            </div>
            <div>
              <label for="completed" class="label text-sm text-gray-600">
                Completed On
              </label>
              {#if editable}
                <DatePicker
                  date={goal.completed_at}
                  onchange={(d) => handleSave({ completed_at: d })}
                />
              {:else}
                <div
                  class="mt-1 text-sm border rounded border-gray-200 px-3 py-2"
                >
                  {#if goal.completed_at}
                    {formatDate(goal.completed_at)}
                  {:else}
                    Not Completed
                  {/if}
                </div>
              {/if}
            </div>
          </div>
        {/if}

        {#if goal.type == "QuarterlyGoal"}
          <div class="col-span-12 mt-2">
            <div>
              <div>
                <div class="border-b border-gray-200">
                  <nav class="-mb-px flex tabs" aria-label="Tabs">
                    {#each tabs as tab}
                      <button
                        onclick={() => (currentTab = tab)}
                        class:active={tab === currentTab}
                        class="tab"
                      >
                        <div class="whitespace-nowrap px-4">{tab}</div>
                      </button>
                    {/each}
                  </nav>
                </div>
              </div>
            </div>
            <div
              class="px-4 py-5 sm:p-6 border rounded-sm"
              style="min-height: 26rem;"
            >
              <div class="col-span-12">
                {#if currentTab === tabs[0]}
                  <GoalNotes
                    {goal}
                    onsave={(payload, title) => handleSave(payload, title)}
                  />
                {:else if currentTab === tabs[1]}
                  <Progresses id={goal.id} type="goal" />
                {:else if currentTab === tabs[2]}
                  <div>
                    <div class="mb-2">
                      <div class="flex items-center mb-1">
                        <ListTodo class="size-5 text-gray-400 mr-2" />
                        Tasks

                        <Loading loading={creatingTask}>
                          <button
                            onclick={addNewTask}
                            class="px-4 ml-4 bg-gray-50 border border-gray-200 text-gray-800 hover:bg-gray-100 rounded-sm shadow-sm text-xs py-1"
                          >
                            + Add Task
                          </button>
                        </Loading>
                      </div>
                    </div>

                    {#each tasks as task (task.id)}
                      <div class="mt-4">
                        <TaskInline
                          {task}
                          ondelete={deleteTask}
                          oncreate={createTask}
                        />
                      </div>
                    {:else}
                      <div class="border-l-4 border-gray-400 shadow-sm mt-4">
                        <div
                          class="py-3 px-5 border-gray-300 border-b border-t flex items-center text-gray-700 text-sm"
                        >
                          No tasks
                        </div>
                      </div>
                    {/each}
                  </div>
                {:else if currentTab === tabs[3]}
                  <GoalMembers {goal} onupdate={updateGoal} />
                {:else if currentTab === tabs[4]}
                  <History id={goal.id} type="Goal" />
                {:else if currentTab === tabs[5]}
                  <Attachments id={goal.id} type="Goal" />
                {/if}
              </div>
            </div>
          </div>
        {:else}
          <GoalNotes
            {goal}
            onsave={(payload, title) => handleSave(payload, title)}
          />
        {/if}
      </div>
    {/if}
  </div>
</div>

<style>
  :root {
    --toastHeight: auto;
  }
</style>
